import React from 'react';
import gettext from '@eb/gettext';
import { SubmissionError } from 'redux-form';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';

import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
} from '@eb/social-login-facebook';

import {
    setAccountExistent,
    updateFormType,
    updateCheckoutFormType,
    showAuthNotification,
    existingSoftAccountCheck,
} from './auth';
import {
    sendResetPasswordEmail,
    sendSoftAccountActivationEmail,
} from './email';
import { socialLogInSoftUser, storeSocialDataOnRedis } from './social';

import {
    trackSIWA_EmailNotFound,
    trackAccountAlreadyConnectedWithFB,
    trackEnterEmailExistingAccount,
    trackAccountConnectedToFacebookAndApple,
    trackAccountConnectedToApple,
    trackEnterEmailSoftAccount,
    trackEnterEmailNewAccount,
    trackCheckoutEnterEmailExistingAccount,
    trackCheckoutEnterEmailSoftAccount,
    trackCheckoutEnterEmailNewAccount,
} from './gaTracking';
import {
    hasFbAndAppleAccountOnly,
    hasFbAccount,
    hasAppleAccount,
} from './lookup';

import {
    BASE_URL,
    LOG_IN_PATH,
    ONBOARDING_REDIRECT_URL_BLACKLIST,
    SIGN_UP_PATH,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LOOK_UP_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
} from '../constants';
import {
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
    SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
    EMAIL_UNEXISTENT_ERROR_MESSAGE,
    EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
    EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
    ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
} from '../constants/messages';

import { notifyParent } from '../utils';

import { AlertChunky } from '@eb/eds-iconography';

/**
 * Returns an object out of the Login Data recieved
 * @param {object} arguments Login Data
 * @param {function} getState Function to be able to get the current state.
 * @param {boolean} newApiCall Signals if it was a call from the new api
 * @returns
 */
export const getLogInFormData = (
    {
        email: formEmail,
        password,
        captcha: recaptchaResponse,
        recaptchaV3ResponseToken,
    },
    getState,
    newApiCall = false,
) => {
    const {
        authentication: { email, forward, pckg, stld },
    } = getState();

    let {
        authentication: { referrer },
    } = getState();

    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer');
    }

    let apiResult = {
        password,
        email: formEmail || email,
        forward,
        referrer,
        pckg,
        stld,
        recaptchaResponse,
        recaptchaV3ResponseToken,
    };

    if (newApiCall) {
        apiResult = {
            password,
            email_address: formEmail || email,
            referrer,
            stld,
            recaptchaV3ResponseToken,
        };
    }
    return apiResult;
};

/**
 * Returns an object out of the Sign up Data recieved
 * @param {object} arguments Sign Up Data
 * @param {function} getState Function to be able to get the current state.
 * @returns
 */
export const getSignUpFormData = (
    {
        firstName,
        lastName,
        password,
        captcha: recaptchaResponse,
        email: formEmail,
    },
    getState,
) => {
    const {
        authentication: { email, forward, referrer, userTypeParams, pckg },
    } = getState();

    const _email = formEmail || email;

    return {
        password,
        firstName,
        lastName,
        recaptchaResponse,
        email: _email,
        forward,
        referrer,
        ...userTypeParams,
        pckg,
    };
};

/**
 * Returns an object out of the Social Login Data recieved
 * @param {object} arguments Social Login Data
 * @param {function} getState Function to be able to get the current state.
 * @returns
 */
export const getSocialLogInFormData = (
    { accessToken, sourceProvider, skipCreation },
    getState,
) => {
    const {
        authentication: { forward, pckg },
    } = getState();

    let {
        authentication: { referrer },
    } = getState();

    // We need to remove the referrer from the local storage, so it won't cause issues on future logins.
    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer');
        localStorage.removeItem('referrer');
    }

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        forward,
        referrer,
        pckg,
    };
};

/**
 * Returns an object out of the Social Signup Data
 * @param {object} arguments  Social Signup Data
 * @param {object} currentState Contains the current State
 * @returns
 */
export const getSocialSignUpFormData = (
    { email, firstName, lastName },
    currentState,
) => {
    const skipCreation = false;
    const {
        authentication: {
            forward,
            referrer,
            pckg,
            socialData: { accessToken, sourceProvider },
        },
    } = currentState;

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        email,
        firstName,
        lastName,
        forward,
        referrer,
        pckg,
    };
};

export const handleResetPasswordAccountStatusResponse = (
    dispatch,
    { exists, can_login: canLogin },
) => {
    let emailAction = sendResetPasswordEmail;

    if (exists && !canLogin) {
        emailAction = sendSoftAccountActivationEmail;
    }

    if (!exists && !canLogin) {
        throw new SubmissionError({ email: gettext('Unknown email provided') });
    }

    return dispatch(emailAction());
};

/**
 * Handles the response from the Api when looking up the email of the user
 * @param {object} response Response from the Api
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 * @param {string} email User email
 */
export const handleLookupUserResponse = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        trackEnterEmailExistingAccount({ state });
        dispatch(updateFormType(LOG_IN_PATH));
    } else if (
        hasFbAndAppleAccountOnly({ socialSSOSettings, userId, signInMethods })
    ) {
        trackAccountConnectedToFacebookAndApple({ state });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (hasFbAccount({ socialSSOSettings, userId, signInMethods })) {
        trackAccountAlreadyConnectedWithFB({
            state,
        });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (hasAppleAccount({ socialSSOSettings, userId, signInMethods })) {
        trackAccountConnectedToApple({ state });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (userId) {
        trackEnterEmailSoftAccount({ state });
        dispatch(sendSoftAccountActivationEmail());
    } else {
        trackEnterEmailNewAccount({ state });
        dispatch(updateFormType(SIGN_UP_PATH));
    }
};

export const handleLookupUserResponseForCheckout = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        trackCheckoutEnterEmailExistingAccount({ state });
        dispatch(updateCheckoutFormType(LOG_IN_PATH));
    } else if (hasFbAccount({ socialSSOSettings, userId, signInMethods })) {
        dispatch(updateCheckoutFormType(SOCIAL_LOG_IN_PATH));
    } else if (userId) {
        trackCheckoutEnterEmailSoftAccount({ state });
        dispatch(
            showAuthNotification({
                message: EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        dispatch(existingSoftAccountCheck({ userHasSoftAccount: true }));
        notifyParent('checkoutLoginSoftAccount');
    } else {
        trackCheckoutEnterEmailNewAccount({ state });
        dispatch(
            showAuthNotification({
                message: EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    }
};
/**
 *
 * @param {*} response
 * @param {*} dispatch
 * @param {*} getState
 * @param {*} email
 * @param {*} accessToken
 * @param {*} sourceProvider
 * @deprecated
 */
/* istanbul ignore next */
export const handleLookupUserResponseForLinking = (
    response,
    dispatch,
    getState,
    email,
    accessToken,
    sourceProvider,
) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: { pathname },
        },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (socialLogin) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (
        isUndefined(userId) &&
        pathname === `${BASE_URL}/${SOCIAL_LOOK_UP_PATH}`
    ) {
        trackSIWA_EmailNotFound(state);
        dispatch(
            showAuthNotification({
                message: EMAIL_UNEXISTENT_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (
        !!userId &&
        includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN)
    ) {
        // for now, we are asking for EB account linking only in the SignIn with Apple flow,
        // in the future, when we ask for linking for other providers we should redefine this condition
        dispatch(
            showAuthNotification({
                message: SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        dispatch(updateFormType(SOCIAL_LOG_IN_PATH));

        /**
         * Storing the new entered email and the access_token from siwa
         * on redis for the forgotPassword flow.
         */
        dispatch(
            storeSocialDataOnRedis({ email, accessToken, sourceProvider }),
        );
    } else if (
        !!userId &&
        isEqual(signInMethods, [SIGN_IN_METHOD_FACEBOOK_LOGIN])
    ) {
        // if only has facebook as login method, we cannot link their account asking for email+password
        dispatch(
            showAuthNotification({
                message: ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (!!userId && isEmpty(signInMethods)) {
        dispatch(socialLogInSoftUser());
        dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));
    }
};

export const shouldOnboardingRedirect = (state) => {
    const referrer = state.authentication.referrer;

    return !find(ONBOARDING_REDIRECT_URL_BLACKLIST, (blacklistedPath) =>
        referrer.includes(blacklistedPath),
    );
};
